import { useParams, Navigate, Link } from "react-router-dom";
import { ArrowRight, Check, ArrowUpRight } from "lucide-react";
import Seo from "@/components/common/Seo";
import PageHeader from "@/components/common/PageHeader";
import { Reveal, KineticHeadingInView } from "@/components/common/Reveal";
import CTASection from "@/components/common/CTASection";
import { SERVICES } from "@/data/content";
import { IMG } from "@/data/images";

const SERVICE_IMG = {
  "fractional-cio": IMG.minimalOffice,
  "executive-advisory": IMG.boardroom,
  "technology-strategy": IMG.dashboard,
  "digital-transformation": IMG.strategyDiscussion,
  "ai-strategy": IMG.dashboard,
  "erp-sap-advisory": IMG.executiveMeeting,
};

const SERVICE_SEO = {
  "fractional-cio": {
    title: "Fractional CIO & Virtual CIO Services",
    keywords: ["Fractional CIO", "Fractional CIO India", "Fractional CIO UAE", "Virtual CIO", "Enterprise Technology Leadership"],
  },
  "executive-advisory": {
    title: "Executive & Board Technology Advisor",
    keywords: ["Executive Technology Advisor", "Board Technology Advisor", "Enterprise Technology Leadership"],
  },
  "technology-strategy": {
    title: "Technology Strategy Consultant",
    keywords: ["Technology Strategy Consultant", "Executive Technology Advisor", "Enterprise Technology Leadership"],
  },
  "digital-transformation": {
    title: "Digital Transformation Advisor",
    keywords: ["Digital Transformation Advisor", "Technology Strategy Consultant", "Enterprise Technology Leadership"],
  },
  "ai-strategy": {
    title: "AI Strategy Consultant",
    keywords: ["AI Strategy Consultant", "Executive Technology Advisor", "Digital Transformation Advisor"],
  },
  "erp-sap-advisory": {
    title: "ERP & SAP Advisory",
    keywords: ["ERP & SAP Advisory", "Enterprise Technology Leadership", "Technology Strategy Consultant"],
  },
};

export default function ServicePage() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/404" replace />;

  const Icon = service.icon;
  const others = SERVICES.filter((s) => s.slug !== slug).slice(0, 3);
  const seo = SERVICE_SEO[slug] || {};
  const serviceLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: service.title,
    serviceType: seo.title || service.title,
    description: service.summary,
    provider: { "@type": "Person", name: "Aftab Azad" },
    areaServed: ["India", "United Arab Emirates", "Singapore", "Middle East", "Global"],
    url: `https://aftabazad.com/services/${slug}`,
  };

  return (
    <>
      <Seo
        title={seo.title || service.title}
        description={service.summary}
        path={`/services/${slug}`}
        keywords={seo.keywords}
        jsonLd={serviceLd}
      />
      <PageHeader
        overline="Advisory service"
        titleLines={service.title.split(" ")}
        subtitle={service.lead}
        testId={`service-header-${slug}`}
      />

      <section data-testid="service-overview" className="bg-white py-24 md:py-32">
        <div className="mx-auto grid max-w-[1400px] gap-16 px-6 md:px-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-navy/5 text-navy">
              <Icon className="h-8 w-8" strokeWidth={1.5} />
            </div>
            <h2 className="mt-8 font-heading text-3xl font-light leading-[1.1] tracking-tight text-navy md:text-4xl">
              <KineticHeadingInView lines={["The engagement, in brief."]} />
            </h2>
            <p className="mt-6 max-w-xl font-body text-lg leading-relaxed text-slate-600">{service.summary}</p>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-[1.5rem] border border-slate-200 bg-surface p-8">
              <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">Business outcomes</p>
              <ul className="mt-6 space-y-4">
                {service.outcomes.map((o) => (
                  <li key={o} className="flex items-start gap-3">
                    <span className="mt-1 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-navy text-white">
                      <Check className="h-3 w-3" strokeWidth={3} />
                    </span>
                    <span className="font-body text-slate-700">{o}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section data-testid="service-image" className="bg-white pb-8">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <Reveal className="overflow-hidden rounded-[2rem] border border-slate-200">
            <img src={SERVICE_IMG[slug] || IMG.boardroom} alt={`${service.title} in practice`} className="h-[280px] w-full object-cover md:h-[440px]" />
          </Reveal>
        </div>
      </section>

      <section data-testid="service-approach" className="bg-surface py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="max-w-2xl">
            <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">The approach</p>
            <h2 className="mt-6 font-heading text-4xl font-light leading-[1.02] tracking-tight text-navy md:text-5xl">
              <KineticHeadingInView lines={["A clear path", "to results."]} />
            </h2>
          </div>
          <div className="mt-16 grid gap-6 md:grid-cols-3">
            {service.approach.map((a, i) => (
              <Reveal key={a.title} delay={i * 0.1} className="relative rounded-[1.5rem] border border-slate-200 bg-white p-8">
                <span className="font-heading text-5xl font-black text-slate-100">0{i + 1}</span>
                <h3 className="mt-4 font-heading text-2xl font-bold tracking-tight text-navy">{a.title}</h3>
                <p className="mt-3 font-body leading-relaxed text-slate-600">{a.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="service-others" className="bg-white py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <h2 className="font-heading text-3xl font-light tracking-tight text-navy md:text-4xl">
              <KineticHeadingInView lines={["Related advisory."]} />
            </h2>
            <Link to="/#services" className="hidden" aria-hidden />
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {others.map((s) => {
              const OtherIcon = s.icon;
              return (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  data-testid={`related-service-${s.slug}`}
                  className="group flex flex-col rounded-[1.5rem] border border-slate-200 bg-white p-8 transition-colors hover:border-royal"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-navy/5 text-navy transition-colors group-hover:bg-navy group-hover:text-white">
                    <OtherIcon className="h-6 w-6" strokeWidth={1.5} />
                  </div>
                  <h3 className="mt-5 font-heading text-xl font-bold text-navy">{s.title}</h3>
                  <span className="mt-4 inline-flex items-center gap-2 font-body text-sm font-semibold text-royal">
                    Explore <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <CTASection
        overline="Start the conversation"
        heading={[`Explore ${service.title.toLowerCase()}`, "for your business."]}
        primary={{ label: "Schedule Executive Consultation", to: "/book" }}
        secondary={{ label: "Download Executive Guide", to: "/resources" }}
      />
    </>
  );
}
